import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useOrdering } from '../../context/OrderingContext'
import general from '../../helpers/general'
import IKImageWrapper from '../IKImageWrapper'
import styles from './CartDrawer.module.scss'

const TRANSITION_MS = 300

function CartLineItem({ item, onIncrement, onDecrement, onRemove }) {
	return (
		<li className={styles.item}>
			<div className={styles.itemThumb}>
				{item.image ? (
					<IKImageWrapper
						image={item.image}
						transformation={[{ width: 160, height: 160 }]}
						loading="lazy"
						lqip={{ active: true }}
						alt=""
						className={styles.itemImg}
					/>
				) : (
					<div className={styles.itemThumbPlaceholder} aria-hidden="true">
						<i className="far fa-image" />
					</div>
				)}
			</div>

			<div className={styles.itemBody}>
				<div className={styles.itemTop}>
					<div className={styles.itemDetails}>
						<span className={styles.itemName}>{item.name}</span>
						{item.variationName && (
							<span className={styles.variationName}>{item.variationName}</span>
						)}
						{item.modifiers?.length > 0 && (
							<ul className={styles.modifierList}>
								{item.modifiers.map((mod) => (
									<li key={`${mod.groupName}-${mod.name}`}>{mod.name}</li>
								))}
							</ul>
						)}
						<span className={styles.itemPrice}>{general.formatPrice(item.price)}</span>
					</div>
					<button
						type="button"
						className={styles.removeBtn}
						data-testid={`remove-${item.lineItemId}`}
						aria-label={`Remove ${item.name}`}
						onClick={() => onRemove(item.lineItemId)}
					>
						<i className="far fa-trash-alt" />
					</button>
				</div>

				<div className={styles.itemBottom}>
					<div className={styles.qtyControls}>
						<button
							type="button"
							data-testid={`decrement-${item.lineItemId}`}
							aria-label={`Decrease ${item.name} quantity`}
							onClick={() => onDecrement(item.lineItemId)}
						>
							−
						</button>
						<span className={styles.qty}>{item.quantity}</span>
						<button
							type="button"
							data-testid={`increment-${item.lineItemId}`}
							aria-label={`Increase ${item.name} quantity`}
							onClick={() => onIncrement(item)}
						>
							+
						</button>
					</div>
					<span className={styles.lineTotal}>
						{general.formatPrice(item.price * item.quantity)}
					</span>
				</div>
			</div>
		</li>
	)
}

export default function CartDrawer() {
	const { cartItems, total, addToCart, removeFromCart, removeLineItem } = useOrdering()
	const history = useHistory()
	const location = useLocation()
	const matchOrderId = useRouteMatch('/order/:id')
	const matchOrderMap = useRouteMatch('/order/:restaurantId/:mapId')
	const [isOpen, setIsOpen] = useState(false)
	const [isClosing, setIsClosing] = useState(false)

	const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0)
	const hasItems = cartItems.length > 0
	const showPortal = (isOpen && hasItems) || isClosing

	const close = useCallback(() => {
		if (!isClosing) setIsClosing(true)
	}, [isClosing])

	const toggle = useCallback(() => {
		if (isClosing) return
		setIsOpen((open) => !open)
	}, [isClosing])

	const handleCheckout = useCallback(() => {
		const checkoutPath = matchOrderMap
			? `/order/${matchOrderMap.params.restaurantId}/${matchOrderMap.params.mapId}/checkout`
			: `/order/${matchOrderId?.params?.id}/checkout`
		setIsClosing(true)
		history.push(checkoutPath)
	}, [history, matchOrderId, matchOrderMap])

	const handleIncrement = useCallback(
		(item) =>
			addToCart(
				{
					_id: item.dishId,
					name: item.name,
					price: item.basePrice ?? item.price,
					image: item.image,
				},
				{
					variation: item.catalogVariationId
						? {
								externalVariationId: item.catalogVariationId,
								name: item.variationName,
								price: item.basePrice ?? item.price,
							}
						: undefined,
					modifiers: item.modifiers || [],
				},
			),
		[addToCart],
	)

	useEffect(() => {
		if (!location.state?.openCart || !hasItems) return
		setIsClosing(false)
		setIsOpen(true)
		history.replace(`${location.pathname}${location.search}`)
	}, [location.state?.openCart, hasItems, history, location.pathname, location.search])

	useEffect(() => {
		if (hasItems || isClosing) return
		setIsOpen(false)
	}, [hasItems, isClosing])

	useEffect(() => {
		if (!isClosing) return undefined

		const timer = setTimeout(() => {
			setIsClosing(false)
			setIsOpen(false)
		}, TRANSITION_MS)

		return () => clearTimeout(timer)
	}, [isClosing])

	useEffect(() => {
		if (cartItems.length === 0 && isOpen && !isClosing) setIsClosing(true)
	}, [cartItems.length, isOpen, isClosing])

	useEffect(() => {
		if (!showPortal) return undefined

		const prevOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'

		return () => {
			document.body.style.overflow = prevOverflow
		}
	}, [showPortal])

	useEffect(() => {
		if (!isOpen || isClosing) return undefined

		const onKeyDown = (event) => {
			if (event.key === 'Escape') setIsClosing(true)
		}

		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [isOpen, isClosing])

	return (
		<>
			<div className={styles.wrapper}>
				<button
					type="button"
					className={styles.cartIcon}
					data-testid="cart-toggle"
					aria-expanded={isOpen && hasItems && !isClosing}
					aria-label="Toggle cart"
					onClick={toggle}
				>
					<span>🛒</span>
					<span data-testid="cart-badge" className={styles.badge}>
						{totalQuantity}
					</span>
				</button>
			</div>

			{showPortal &&
				createPortal(
					<div
						className={`${styles.overlay} ${isClosing ? styles.overlayExit : styles.overlayEnter}`}
					>
						<button
							type="button"
							className={styles.backdrop}
							data-testid="cart-backdrop"
							aria-label="Close cart"
							onClick={close}
						/>
						<aside
							className={styles.panel}
							role="dialog"
							aria-modal="true"
							aria-label="Your order"
							data-testid="cart-drawer"
						>
							<header className={styles.header}>
								<h2 className={styles.title}>Your order</h2>
								<button
									type="button"
									className={styles.closeBtn}
									data-testid="cart-close"
									aria-label="Close cart"
									onClick={close}
								>
									×
								</button>
							</header>

							<div className={styles.content}>
								<ul className={styles.itemList}>
									{cartItems.map((item) => (
										<CartLineItem
											key={item.lineItemId}
											item={item}
											onIncrement={handleIncrement}
											onDecrement={removeFromCart}
											onRemove={removeLineItem}
										/>
									))}
								</ul>
							</div>

							<footer className={styles.footer}>
								<div className={styles.subtotal}>
									<span>Subtotal</span>
									<span data-testid="cart-subtotal">{general.formatPrice(total)}</span>
								</div>
								<div className={styles.total}>
									<span>Total</span>
									<span data-testid="cart-total">{general.formatPrice(total)}</span>
								</div>
								<button
									type="button"
									className={styles.checkoutBtn}
									data-testid="cart-checkout"
									onClick={handleCheckout}
								>
									Checkout
								</button>
							</footer>
						</aside>
					</div>,
					document.body
				)}
		</>
	)
}
